// src/components/application/FundingApplication.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847', goldLight: '#DBDDAC',
  cream: '#F0EFEB', warm: '#F7F6F3', sand: '#E2E0DB', white: '#FFFFFF',
  green: '#2E7D32', greenBg: '#e8f5e9', red: '#c0392b', redBg: '#fde8e8',
  blue: '#1565C0', blueBg: '#e3f2fd', amber: '#F57F17', amberBg: '#fff8e1',
};

const STATUS_CONFIG = {
  pending:        { label: 'Pending',            icon: '⬜', color: '#6b7280', bg: '#f3f4f6' },
  uploaded:       { label: 'Uploaded',           icon: '📤', color: C.blue,   bg: C.blueBg  },
  accepted:       { label: 'Accepted',           icon: '✅', color: C.green,  bg: C.greenBg },
  rejected:       { label: 'Needs Resubmission', icon: '❌', color: C.red,    bg: C.redBg   },
  not_applicable: { label: 'N/A',                icon: '➖', color: '#9e9e9e', bg: '#f5f5f5' },
};

const APP_STATUS_CONFIG = {
  draft:             { label: 'Draft',               color: '#6b7280' },
  questionnaire_done:{ label: 'Questionnaire Done',  color: '#92400e' },
  in_progress:       { label: 'In Progress',         color: '#065f46' },
  under_review:      { label: 'Under Review',        color: '#1e40af' },
  revision_needed:   { label: 'Revision Needed',     color: '#9a3412' },
  submitted_to_ded:  { label: 'Submitted to DED',    color: '#1e40af' },
  approved:          { label: 'Approved',            color: '#065f46' },
  denied:            { label: 'Denied',              color: '#991b1b' },
  withdrawn:         { label: 'Withdrawn',           color: '#6b7280' },
  completed:         { label: 'Completed',           color: '#065f46' },
};

export default function FundingApplication() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { profile, isStaff, isClerk, signOut } = useAuth();

  const [app, setApp] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (applicationId) loadApplication();
  }, [applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadApplication() {
    setLoading(true);
    setError('');
    try {
      const { data: appData, error: appErr } = await supabase
        .from('applications')
        .select('*, municipalities(name, county), users!applications_created_by_fkey(name, email)')
        .eq('id', applicationId)
        .single();

      if (appErr || !appData) {
        setError('Application not found or you do not have permission to view it.');
        setLoading(false);
        return;
      }
      setApp(appData);

      const { data: docs } = await supabase
        .from('application_documents')
        .select('*')
        .eq('application_id', applicationId)
        .order('is_required', { ascending: false })
        .order('exhibit_code',  { ascending: true })
        .order('document_name', { ascending: true });
      setDocuments(docs || []);

      const { data: notesData } = await supabase
        .from('staff_notes')
        .select('*, users!staff_notes_author_id_fkey(name)')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: false });
      setNotes(notesData || []);
    } catch (err) {
      console.error('Load error:', err);
      setError('Failed to load application.');
    }
    setLoading(false);
  }

  async function handleFileUpload(docId, file) {
    if (!file) return;
    setUploadingDocId(docId);
    setError('');
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `applications/${applicationId}/${docId}-${Date.now()}.${fileExt}`;

      const { error: uploadErr } = await supabase.storage
        .from('application-documents')
        .upload(filePath, file, { upsert: true });
      if (uploadErr) throw uploadErr;

      const { error: updateErr } = await supabase
        .from('application_documents')
        .update({
          status: 'uploaded',
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: profile?.id,
          uploaded_at: new Date().toISOString(),
        })
        .eq('id', docId);
      if (updateErr) throw updateErr;

      await loadApplication();
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed: ' + (err.message || 'Unknown error'));
    }
    setUploadingDocId(null);
  }

  async function updateDocStatus(docId, newStatus) {
    try {
      await supabase
        .from('application_documents')
        .update({ status: newStatus, reviewed_by: profile?.id, reviewed_at: new Date().toISOString() })
        .eq('id', docId);
      await loadApplication();
    } catch (err) {
      console.error('Status update error:', err);
    }
  }

  async function updateAppStatus(newStatus) {
    try {
      const updates = { status: newStatus };
      if (newStatus === 'submitted_to_ded') updates.submitted_at = new Date().toISOString();
      if (newStatus === 'approved')         updates.approved_at  = new Date().toISOString();
      await supabase.from('applications').update(updates).eq('id', applicationId);
      await loadApplication();
    } catch (err) {
      console.error('App status update error:', err);
    }
  }

  async function addNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await supabase.from('staff_notes').insert({
        application_id: applicationId,
        author_id: profile?.id,
        note_text: newNote.trim(),
        note_type: 'general',
        is_visible_to_clerk: true,
      });
      setNewNote('');
      await loadApplication();
    } catch (err) {
      console.error('Note error:', err);
    }
    setSavingNote(false);
  }

  const filteredDocs = documents.filter(d => filter === 'all' ? true : d.status === filter);
  const docStats = {
    total:    documents.filter(d => d.status !== 'not_applicable').length,
    pending:  documents.filter(d => d.status === 'pending').length,
    uploaded: documents.filter(d => d.status === 'uploaded').length,
    accepted: documents.filter(d => d.status === 'accepted').length,
    rejected: documents.filter(d => d.status === 'rejected').length,
  };

  // ── Loading state ──
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.warm }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 36, height: 36, border: '3px solid #E2E0DB', borderTopColor: C.gold,
          borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
        }} />
        <p style={{ color: C.slate, fontSize: 14, margin: 0 }}>Loading application...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  // ── Error / not found state ──
  if (error && !app) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, background: C.warm }}>
      <p style={{ color: C.red, fontSize: 15 }}>{error}</p>
      <button
        onClick={() => navigate('/dashboard')}
        style={{ background: C.gold, color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
      >
        Back to Dashboard
      </button>
    </div>
  );

  const appStatus = APP_STATUS_CONFIG[app?.status] || APP_STATUS_CONFIG.draft;

  return (
    <div style={{ minHeight: '100vh', background: C.warm, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── TOP NAV BAR ── */}
      <div style={{
        background: C.navy, padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontFamily: "'Georgia', serif", fontSize: 18, fontWeight: 700, color: C.cream }}>
            PADD Hub
          </span>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'transparent', border: `1px solid ${C.slate}`,
              color: C.sand, padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            }}
          >
            ← Dashboard
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: C.sand }}>
            {profile?.name || profile?.email}
            {isStaff && <span style={{ marginLeft: 8, background: C.gold, color: '#fff', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>Staff</span>}
          </span>
          <button
            onClick={async () => { await signOut(); navigate('/login'); }}
            style={{
              background: 'transparent', border: `1px solid ${C.slate}`,
              color: C.sand, padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            }}
          >
            Log Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: '28px 20px' }}>

        {/* ── APPLICATION HEADER ── */}
        <div style={{
          background: C.white, borderRadius: 12, border: `1px solid ${C.sand}`,
          padding: '20px 24px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontFamily: "'Georgia', serif", fontSize: 22, fontWeight: 700, color: C.navy, margin: '0 0 6px' }}>
                {app.project_name || 'Untitled Application'}
              </h1>
              <p style={{ fontSize: 13, color: C.slate, margin: 0 }}>
                {app.municipalities?.name}
                {app.municipalities?.county ? ` — ${app.municipalities.county} County` : ''}
              </p>
              {app.submitted_at && (
                <p style={{ fontSize: 12, color: '#9e9e9e', margin: '4px 0 0' }}>
                  Submitted: {new Date(app.submitted_at).toLocaleDateString()}
                </p>
              )}
              {app.users?.name && (
                <p style={{ fontSize: 12, color: '#9e9e9e', margin: '2px 0 0' }}>
                  Created by: {app.users.name}
                </p>
              )}
            </div>
            <span style={{
              padding: '6px 18px', borderRadius: 20, fontSize: 13, fontWeight: 700,
              color: appStatus.color, border: `1.5px solid ${appStatus.color}`,
              background: `${appStatus.color}18`, whiteSpace: 'nowrap', alignSelf: 'flex-start',
            }}>
              {appStatus.label}
            </span>
          </div>
        </div>

        {/* ── STAFF STATUS ACTION PANEL ── */}
        {isStaff && (
          <div style={{
            background: C.white, borderRadius: 12, border: `1px solid ${C.sand}`,
            padding: '14px 20px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Change Status:</span>
            <select
              value={app.status}
              onChange={(e) => updateAppStatus(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.sand}`,
                fontSize: 13, color: C.navy, background: C.white, cursor: 'pointer',
              }}
            >
              {Object.entries(APP_STATUS_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* ── INLINE ERROR BANNER ── */}
        {error && (
          <div style={{
            background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8,
            padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.red,
          }}>
            {error}
          </div>
        )}

        {/* ── DOCUMENT STATS ROW ── */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total',               value: docStats.total,    color: C.navy,   bg: C.cream    },
            { label: 'Pending',             value: docStats.pending,  color: '#6b7280', bg: '#f3f4f6'  },
            { label: 'Uploaded',            value: docStats.uploaded, color: C.blue,   bg: C.blueBg   },
            { label: 'Accepted',            value: docStats.accepted, color: C.green,  bg: C.greenBg  },
            { label: 'Needs Resubmission',  value: docStats.rejected, color: C.red,    bg: C.redBg    },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              flex: '1 1 110px', background: C.white, borderRadius: 10,
              border: `1px solid ${C.sand}`, padding: '12px 14px', textAlign: 'center',
            }}>
              <div style={{
                fontSize: 26, fontWeight: 700, color, marginBottom: 2,
              }}>
                {value}
              </div>
              <div style={{ fontSize: 11, color: C.slate, fontWeight: 600 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* ── FILTER TABS ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { key: 'all',      label: 'All' },
            { key: 'pending',  label: 'Pending' },
            { key: 'uploaded', label: 'Uploaded' },
            { key: 'accepted', label: 'Accepted' },
            { key: 'rejected', label: 'Needs Resubmission' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                fontWeight: filter === key ? 700 : 400,
                background: filter === key ? C.navy : C.white,
                color: filter === key ? C.cream : C.slate,
                border: `1px solid ${filter === key ? C.navy : C.sand}`,
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── DOCUMENT LIST ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
          {filteredDocs.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '40px 20px', color: C.slate,
              fontSize: 14, background: C.white, borderRadius: 10, border: `1px solid ${C.sand}`,
            }}>
              No documents match this filter.
            </div>
          )}

          {filteredDocs.map((doc) => {
            const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
            const isUploading = uploadingDocId === doc.id;
            const canUpload = isClerk && (doc.status === 'pending' || doc.status === 'rejected');
            const canReview = isStaff && doc.status === 'uploaded';

            return (
              <div key={doc.id} style={{
                background: C.white, borderRadius: 10, border: `1px solid ${C.sand}`,
                padding: '14px 18px', display: 'flex', alignItems: 'center',
                gap: 12, flexWrap: 'wrap',
              }}>

                {/* Exhibit code badge */}
                {doc.exhibit_code && (
                  <span style={{
                    minWidth: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 8, background: C.cream, color: C.navy,
                    fontSize: 12, fontWeight: 700, border: `1px solid ${C.sand}`, flexShrink: 0,
                  }}>
                    {doc.exhibit_code}
                  </span>
                )}

                {/* Document name + required badge */}
                <div style={{ flex: 1, minWidth: 140 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>
                    {doc.document_name}
                  </span>
                  {doc.is_required && (
                    <span style={{
                      marginLeft: 8, padding: '1px 8px', borderRadius: 10,
                      background: '#fff3cd', color: '#856404', fontSize: 11, fontWeight: 600,
                    }}>
                      Required
                    </span>
                  )}
                  {doc.uploaded_at && (
                    <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 2 }}>
                      Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <span style={{
                  padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                  color: sc.color, background: sc.bg, whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {sc.icon} {sc.label}
                </span>

                {/* Clerk: upload button */}
                {canUpload && (
                  <label style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: isUploading ? C.sand : C.blue, color: '#fff',
                    cursor: isUploading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                    display: 'inline-block', flexShrink: 0,
                  }}>
                    {isUploading ? 'Uploading…' : 'Upload File'}
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      disabled={isUploading}
                      onChange={(e) => {
                        if (e.target.files[0]) handleFileUpload(doc.id, e.target.files[0]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}

                {/* Staff: accept / reject buttons */}
                {canReview && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => updateDocStatus(doc.id, 'accepted')}
                      style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        background: C.green, color: '#fff', border: 'none', cursor: 'pointer',
                      }}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => updateDocStatus(doc.id, 'rejected')}
                      style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        background: C.red, color: '#fff', border: 'none', cursor: 'pointer',
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}

                {/* Download button — shown whenever a file has been uploaded */}
                {doc.file_path && (
                  <button
                    onClick={async () => {
                      const { data } = await supabase.storage
                        .from('application-documents')
                        .createSignedUrl(doc.file_path, 60);
                      if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      background: C.cream, color: C.navy, border: `1px solid ${C.sand}`,
                      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    ↓ Download
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── NOTES SECTION ── */}
        <div style={{
          background: C.white, borderRadius: 12, border: `1px solid ${C.sand}`, padding: '20px 24px',
        }}>
          <h3 style={{
            fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 700,
            color: C.navy, margin: '0 0 16px',
          }}>
            Notes
          </h3>

          {notes.length === 0 && (
            <p style={{ fontSize: 13, color: '#9e9e9e', margin: '0 0 16px' }}>No notes yet.</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {notes.map((note) => (
              <div key={note.id} style={{
                background: C.cream, borderRadius: 8, padding: '12px 16px',
                border: `1px solid ${C.sand}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.navy }}>
                    {note.users?.name || 'Staff'}
                  </span>
                  <span style={{ fontSize: 11, color: '#9e9e9e' }}>
                    {new Date(note.created_at).toLocaleString()}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: C.slate, margin: 0, lineHeight: 1.6 }}>
                  {note.note_text}
                </p>
              </div>
            ))}
          </div>

          {/* Add note */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note visible to all parties…"
              rows={3}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 8,
                border: `1.5px solid ${C.sand}`, fontSize: 13,
                fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                color: C.navy,
              }}
              onFocus={(e) => (e.target.style.borderColor = C.gold)}
              onBlur={(e)  => (e.target.style.borderColor = C.sand)}
            />
            <button
              onClick={addNote}
              disabled={savingNote || !newNote.trim()}
              style={{
                padding: '10px 22px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: savingNote || !newNote.trim() ? C.sand : C.gold,
                color: savingNote || !newNote.trim() ? C.slate : '#fff',
                border: 'none',
                cursor: savingNote || !newNote.trim() ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {savingNote ? 'Saving…' : 'Add Note'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
